import errorHelper from "./error-helper";
import htmlEntities from "../scripts/html-entities";
import modalDeleteSensorsGroup from "../templates/dashboard/modal-delete-group";
import CacheInstance from "./cache";
import modalEditSensorsGroup from "../templates/dashboard/modal-edit-group";
import isFeatureAllowed from "../util/is-feature-allowed";
import revealInScroller from "../scripts/reveal-in-scroller";
import { getIdList, setParams } from "../scripts/url-state";

export default class SensorsGroupsManager {
  /**
   * @param {{sidebar: HTMLElement, label: string, urlState?: boolean}} options
   *   urlState: the selection is also kept in the page's address
   *   (?account=, ?group=), and an address naming one opens on it
   */
  constructor({ sidebar, label, urlState = false }) {
    this.sidebar = sidebar;
    this.label = label;
    this.urlState = urlState;
    // what a link asked for that this login can't see: { accounts, groups }
    this.linkMissing = null;
    this.ROOT_SENSORS_ID = 0;

    this.groups = [];
    this.groupsMap = {};
    this.sensors = [];

    this.selectedNodeIds = new Set();
    this.activeGroupId = null;
    this.activeGroupIds = new Set();

    this.expandedGroupIds = new Set();
    this.expandedSensorGroups = new Set();

    this.currentGroup = null;

    this._initialized = false;
    this._selectionCacheKey = this._getSelectionCacheKey();
  }

  async init() {
    await this.loadGroups();

    // Restore selection (or default to All Sensors)
    this._restoreSelectionFromCacheOrDefault();

    this.actions();

    document.addEventListener("dragstart", e => {

      if(!isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER)) {
        return;
      }

      // the grid calls off a drag that didn't start on a row's handle
      if (e.defaultPrevented) {
        return;
      }

      const target = e.target;

      // Detect drag source
      const gridRow = target?.closest?.('.data-grid-row[data-sensor-row="1"]');
      const sensorEl = target?.closest?.(".folder-item.sensor-item");

      let ownerId = null;
      let sourceGroupId = null;

      if (gridRow) {
        const sid = gridRow.dataset.sensor;
        const sensor = this.getSensorById(sid);
        ownerId = this._getSensorOwnerId(sensor);
      } else if (sensorEl) {
        const sid = sensorEl.dataset.sensor;
        const sensor = this.getSensorById(sid);
        ownerId = this._getSensorOwnerId(sensor);
        sourceGroupId = Number(sensorEl.dataset.group);
      }

      // Highlight only real group nodes that match this sensor ownerId (under All Accounts)
      this.sidebar
        .querySelectorAll('.folder-header[data-droppable="1"]')
        .forEach(el => {
          const gid = Number(el.dataset.id);
          const node = this.groupsMap?.[gid];
          if (!node) return;

          const nodeScopeId = Number(node?.sub_account_id ?? node?.account_id ?? null);
          const isAccountScoped = Number.isFinite(nodeScopeId) && nodeScopeId > 0;

          // Account-scoped groups must match ownerId.
          // Global groups (scope 0) are allowed for all.
          const matchesOwner =
            ownerId == null ||
            (isAccountScoped
              ? nodeScopeId === Number(ownerId)
              : (Number.isFinite(nodeScopeId) &&
                (nodeScopeId === 0 || nodeScopeId === Number(ownerId))));

          if (!matchesOwner) return;


          // if dragging from within a group in the sidebar, do not highlight the same group
          if (sourceGroupId && Number.isFinite(sourceGroupId) && gid === sourceGroupId) return;

          el.classList.add("droppable-target");
        });

      // ALSO: show the sidebar remove zone when dragging starts
      // - If dragging a sidebar sensor, show that sensor's sourceGroup remove zone
      // - If dragging a grid row, show the active selected group remove zone (when it is a real group)
      let removeZoneGroupId = null;

      if (sourceGroupId && Number.isFinite(sourceGroupId) && sourceGroupId > 0) {
        removeZoneGroupId = sourceGroupId;
      } else {
        const activeNode = this.groupsMap?.[this.activeGroupId];
        if (this._isRealGroupNode(activeNode)) {
          removeZoneGroupId = Number(activeNode.id);
        }
      }

      if (removeZoneGroupId != null) {
        const zone = this.sidebar.querySelector(
          `.item-remove-zone[data-for="${removeZoneGroupId}"]`
        );
        if (zone) zone.classList.add("is-active");
      }
    });

    document.addEventListener("dragend", () => {

      if(!isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER)) {
        return;
      }

      this.sidebar
        .querySelectorAll('.folder-header[data-droppable="1"]')
        .forEach(el => el.classList.remove("droppable-target", "droppable-over"));

      // clear any sidebar remove-zone highlighting
      this.sidebar
        .querySelectorAll(".item-remove-zone")
        .forEach(el => el.classList.remove("is-active", "drag-over"));
    });

    document.addEventListener("sensors:visible", async () => {
      await this.loadGroups();
      this.render();
    });

    document.addEventListener("sensors:get", async e => {
      this.sensors = e.detail.sensors;
      this.render();
    });

    // the sidebar scrolls, so nothing can hang out of it: an inline "new
    // group" form has no room once it is dragged down to the icon rail
    document.addEventListener("sidebarResized", e => {
      if (e.detail?.sidebar === this.sidebar && this.sidebar.classList.contains("is-tiny")) {
        this._closeInlineCreate();
      }
    });

    this.render();
    this._revealActiveFolder();

    // Ensure filters apply on first load (Enterprise listens to this)
    this._emitSelect(Array.from(this.selectedNodeIds || []));
    this._initialized = true;
  }

  actions() {
    $("body").on("dragstart", ".folder-item.sensor-item", e => {

      if(!isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER)) {
        return;
      }

      const nativeEvt = e.originalEvent;
      const $row = $(e.currentTarget);
      const draggedId = $row.data("sensor");
      const groupId = Number($row.data("group"));

      const sourceGroupId = groupId > 0 ? groupId : null;

      const $zone = sourceGroupId
        ? $("body").find(`.item-remove-zone[data-for="${sourceGroupId}"]`)
        : $();

      if ($zone.length) $zone.addClass("is-active");

      const sensorIds = [draggedId];

      const payload = {
        type: "sensors",
        ids: sensorIds,
        sourceGroupId
      };

      nativeEvt.dataTransfer.setData("application/json", JSON.stringify(payload));

      const emptyImg = new Image();
      emptyImg.src =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAADFBMVEX///8AAABmZmZU3JJKAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg==";
      nativeEvt.dataTransfer.setDragImage(emptyImg, 0, 0);

      const sensor = this.getSensorById(draggedId);
      const text = `${sensor ? sensor.sensor_name : ""}`;

      const helperEl = document.createElement("div");
      helperEl.innerHTML = text;

      Object.assign(helperEl.style, {
        position: "fixed",
        top: "0px",
        left: "0px",
        padding: "4px 8px",
        background: "#ffffff",
        border: "1px solid #ccc",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        fontSize: "12px",
        pointerEvents: "none",
        zIndex: "9999",
        opacity: "1",
        userSelect: "none",
        whiteSpace: "nowrap"
      });

      document.body.appendChild(helperEl);

      function onDragOver(moveEvt) {
        const x = moveEvt.clientX - helperEl.offsetWidth / 2;
        const y = moveEvt.clientY - helperEl.offsetHeight / 2;
        helperEl.style.transform = `translate(${x}px, ${y}px)`;
      }

      document.addEventListener("dragover", onDragOver);

      function onDragEnd() {
        document.removeEventListener("dragover", onDragOver);
        document.removeEventListener("dragend", onDragEnd);
        if (helperEl.parentNode) helperEl.parentNode.removeChild(helperEl);

        if ($zone.length) $zone.removeClass("is-active");
      }

      document.addEventListener("dragend", onDragEnd);
    });

    $(this.sidebar).on("click", '[data-action="toggle_group_visibility"]', async e => {
      const groupId = Number(e.currentTarget.dataset.for);
      const visible = Number(e.currentTarget.dataset.visible);

      const sub_account_id = this._resolveSubAccountIdForNodeId(groupId);

      const res = await window.apiRequest("post", `/groups/sensors/${groupId}/visible`, {
        visible,
        sub_account_id
      });

      console.log(`/groups/sensors/${groupId}/visible`, res);

      if (res.type === "success") {
        const evt = new CustomEvent("sensors:visible", { detail: { group_id: groupId } });
        document.dispatchEvent(evt);
      }
    });

    $(this.sidebar).on("click", '[data-action="add_group"]', e => {
      const groupId = Number(e.currentTarget.dataset.for);
      const li = this.sidebar.querySelector(`.folder-item[data-for="${groupId}"]`);

      if (!li) return;

      this._closeInlineCreate();
      this._startInlineCreate(groupId, li);
    });

    this.sidebar.addEventListener("click", e => {
      const li = e.target.closest("li.folder-item.new-group:not(.creating)");
      if (!li) return;
      e.stopPropagation();

      const parentId = Number(li.dataset.for);
      this._closeInlineCreate();
      this._startInlineCreate(parentId, li);
    });

    // Sidebar sensor click: single = open flyout, double = open + filter to group + highlight
    let sensorClickCount = 0;
    const SENSOR_DBLCLICK_DELAY = 210;

    this._lastClickedSensorKey = null;
    this._sensorClickTimer = null;

    $("body").on("click", ".groups-sidebar .folder-item.sensor-item .item-entry", e => {
      const sensorEl = e.currentTarget.closest(".folder-item.sensor-item");
      if (!sensorEl) return;

      const sensorId = sensorEl.dataset.sensor;
      const contextNodeId = Number(sensorEl.dataset.group);

      const key = `${sensorId}:${contextNodeId}`;
      if (this._lastClickedSensorKey !== key) {
        sensorClickCount = 0;
        this._lastClickedSensorKey = key;
      }

      sensorClickCount++;

      if (this._sensorClickTimer) clearTimeout(this._sensorClickTimer);

      this._sensorClickTimer = setTimeout(() => {
        const mode = sensorClickCount === 2 ? "double" : "single";

        const targetGroupId =
          mode === "double"
            ? this._resolveGroupIdForSensorClick(sensorId, contextNodeId)
            : null;

        document.dispatchEvent(
          new CustomEvent("sidebarSensorClick", {
            detail: {
              sensor_id: sensorId,
              mode,
              group_id: targetGroupId,
              context_node_id: contextNodeId
            }
          })
        );

        sensorClickCount = 0;
      }, SENSOR_DBLCLICK_DELAY);
    });

    $("body").on("click", ".more-toggle", e => {
      const groupId = Number(e.currentTarget.dataset.groupId);
      if (this.expandedSensorGroups.has(groupId)) {
        this.expandedSensorGroups.delete(groupId);
      } else {
        this.expandedSensorGroups.add(groupId);
      }
      this.render();
    });

    let clickCount = 0;
    const DBLCLICK_DELAY = 210;

    this.lastClickedGroupId = -1;

    $("body").on("click", ".folder-label", e => {
      const id = Number(e.currentTarget.dataset.id);
      const shift = e.shiftKey;

      if (this.lastClickedGroupId !== id) {
        clickCount = 0;
        this.lastClickedGroupId = id;
      }

      clickCount++;

      setTimeout(() => {
        if (clickCount === 0) return;

        if (clickCount !== 2) {
          if (this.selectedNodeIds?.has(id) && id !== 0) {
            this._onFolderClick(id, "delete");
          } else {
            if (shift) {
              this._onFolderClick(id, "add");
            } else {
              this._onFolderClick(id, "select");
            }
          }
        } else {
          // prevent collapsing the root
          if (id !== 0) {
            if (this.expandedGroupIds.has(id)) {
              this.expandedGroupIds.delete(id);
            } else {
              this.expandedGroupIds.add(id);
            }
          }

          // root stays open so accounts remain visible
          this.expandedGroupIds.add(0);

          this.render();
        }

        clickCount = 0;
      }, DBLCLICK_DELAY);
    });

    $("body").on("click", "a.breadcrumb", e => {
      e.preventDefault();
      const id = Number(e.currentTarget.dataset.id);
      this._onFolderClick(id);
    });

    document.addEventListener("groupDeleted", async e => {
      const detail = e?.detail || {};

      const deletedId = Number(detail.group_id ?? detail.groupId ?? detail.id ?? detail?.group?.id);

      const wasSelected =
        Number.isFinite(deletedId) &&
        (this.activeGroupId === deletedId || this.selectedNodeIds?.has?.(deletedId));

      if (!wasSelected) {
        await this.loadGroups();
        this.render();
        return;
      }

      // Prefer explicit parent_id from event (if present)
      let targetId = Number(detail.parent_id);
      if (!Number.isFinite(targetId)) targetId = null;

      // Infer parent/account/root from current map BEFORE reload
      let fallbackAccountNodeId = null;

      const deletedNode = Number.isFinite(deletedId) ? this.groupsMap?.[deletedId] : null;
      const deletedScopeId = Number(deletedNode?.account_id ?? deletedNode?.sub_account_id ?? null);
      const fallbackRootId = this.ROOT_SENSORS_ID;

      if (targetId == null && Number.isFinite(deletedId)) {
        const node = this.groupsMap?.[deletedId];
        if (node) {
          const parents = Array.isArray(node.parents) ? node.parents : [];
          const immediateParentId = parents.length ? Number(parents[parents.length - 1]) : null;

          // Only treat a positive id as a "real parent group"
          if (Number.isFinite(immediateParentId) && immediateParentId > 0) {
            targetId = immediateParentId;
          }

          const aid = Number(node.account_id ?? node.sub_account_id);
          if (Number.isFinite(aid) && aid > 0) {
            fallbackAccountNodeId = this._accountNodeId(aid);
          }
        }
      }

      if (targetId == null) {
        targetId = fallbackAccountNodeId != null ? fallbackAccountNodeId : fallbackRootId;
      }

      await this.loadGroups();

      // If target doesn't exist after reload, fall back to account, then correct root
      if (targetId !== fallbackRootId && !this.groupsMap?.[targetId]) {
        targetId =
          fallbackAccountNodeId != null && this.groupsMap?.[fallbackAccountNodeId]
            ? fallbackAccountNodeId
            : fallbackRootId;
      }

      this._onFolderClick(targetId, "select");
    });

  }

  getGroups() {
    return this.groups;
  }

  getGroupById(groupId) {
    return this.groupsMap[groupId];
  }

  getSensorById(sensorId) {
    const matched = this.sensors.filter(s => s.sensor_id == sensorId || s.id == sensorId);
    if (matched && matched.length) {
      return matched[0];
    }
    return null;
  }

  getGroupDetails(groupId) {
    const pathIds = this._findPath(this.groups, groupId);

    if (!pathIds) {
      return [];
    }

    return pathIds.map(id => {
      return {
        id: id,
        group_name: this.groupsMap[id].group_name
      };
    });
  }

  _getSelectionCacheKey() {
    const safe = String(this.label || "all_sensors")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return `groups_sidebar_selection_${safe}`;
  }

  _readSelectionCache() {
    const key = this._selectionCacheKey || this._getSelectionCacheKey();

    // Prefer CacheInstance when available
    try {
      const v = CacheInstance.get(key);
      if (v && v.value) return v.value;
    } catch (_) {}

    // Fallback: raw localStorage
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  _writeSelectionCache(payload) {
    const key = this._selectionCacheKey || this._getSelectionCacheKey();

    try {
      CacheInstance.save(key, payload);
      return;
    } catch (_) {}

    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (_) {}
  }

  _applySelection(selectedIds, activeId) {
    const ids = (selectedIds || [])
      .map(n => Number(n))
      .filter(n => Number.isFinite(n));

    let active = Number(activeId);
    if (!Number.isFinite(active)) active = ids[ids.length - 1];

    // Any root selection is exclusive (All Sensors OR All Accounts)
    const rootPicked = ids
      .map(id => ({ id, node: this.groupsMap?.[id] }))
      .find(x => x.node && x.node.node_type === "root");

    const normalizedIds = rootPicked
      ? [rootPicked.id]
      : ids.length
        ? ids
        : [this.ROOT_SENSORS_ID];

    if (rootPicked) active = rootPicked.id;

    if (!normalizedIds.includes(active)) {
      active = normalizedIds[normalizedIds.length - 1] ?? this.ROOT_SENSORS_ID;
    }

    this.selectedNodeIds = new Set(normalizedIds);
    this.activeGroupId = active;

    // Highlight: explicit selections + their ancestors
    const newActive = new Set();
    this.selectedNodeIds.forEach(id => {
      const pathIds = this._findPath(this.groups, id) || [id];
      pathIds.forEach(pid => newActive.add(pid));
    });

    this.activeGroupIds = newActive;

    // Expand all active nodes so selection is visible
    this.expandedGroupIds = new Set();
    this.activeGroupIds.forEach(id => this.expandedGroupIds.add(id));

    // Keep both top-level roots visible/open
    this.expandedGroupIds.add(this.ROOT_SENSORS_ID);

    this.currentGroup = this._findNode(this.groups, this.activeGroupId) || null;
  }

  // ?account= and ?group= (a link, or this page reloaded) win over the
  // remembered selection. Ids this login can't see are left out and kept in
  // linkMissing, for the page to say so; when none is left the view starts
  // at All Sensors, never at a remembered account the link didn't name.
  _selectionFromUrl() {
    if (!this.urlState) return null;
    const accounts = getIdList("account");
    const groups = getIdList("group");
    if (!accounts.length && !groups.length) return null;

    const ids = [];
    const missing = { accounts: [], groups: [] };
    accounts.forEach(aid => {
      const id = this._accountNodeId(aid);
      if (this.groupsMap?.[id]) ids.push(id);
      else missing.accounts.push(aid);
    });
    groups.forEach(gid => {
      if (this._isRealGroupNode(this.groupsMap?.[gid])) ids.push(gid);
      else missing.groups.push(gid);
    });
    this.linkMissing = missing.accounts.length || missing.groups.length ? missing : null;
    return ids.length ? ids : [this.ROOT_SENSORS_ID];
  }

  _saveSelectionToUrl() {
    if (!this.urlState) return;
    const accounts = [];
    const groups = [];
    Array.from(this.selectedNodeIds || []).forEach(id => {
      const node = this.groupsMap?.[id];
      if (!node) return;
      if (this._isRealGroupNode(node)) {
        groups.push(Number(node.id));
      } else if (["account", "all_sensors", "unmonitored"].includes(node.node_type)) {
        const aid = Number(node.account_id ?? node.sub_account_id);
        if (aid > 0 && !accounts.includes(aid)) accounts.push(aid);
      }
    });
    setParams({ account: accounts, group: groups });
  }

  _restoreSelectionFromCacheOrDefault() {
    const linked = this._selectionFromUrl();
    if (linked) {
      this._applySelection(linked, linked[linked.length - 1]);
      this._saveSelectionToCache();
      return;
    }

    const cached = this._readSelectionCache();

    const cachedSelected = Array.isArray(cached?.selectedNodeIds) ? cached.selectedNodeIds : null;
    const cachedActive = cached?.activeGroupId;

    // Keep only ids that exist now (allow 0/root)
    const validSelected = (cachedSelected || [])
      .map(n => Number(n))
      .filter(n => Number.isFinite(n))
      .filter(id => id === 0 || !!this.groupsMap?.[id]);

    let active = Number(cachedActive);
    const activeValid = Number.isFinite(active) && (active === 0 || !!this.groupsMap?.[active]);
    if (!activeValid) active = validSelected[validSelected.length - 1] ?? 0;

    // Default: All Sensors (root), with accounts collapsed (root stays open)
    const finalSelected = validSelected.length ? validSelected : [0];

    this._applySelection(finalSelected, active);
    this._saveSelectionToCache();
  }

  _saveSelectionToCache() {
    const payload = {
      selectedNodeIds: Array.from(this.selectedNodeIds || []),
      activeGroupId: this.activeGroupId
    };
    this._writeSelectionCache(payload);
    this._saveSelectionToUrl();
  }

  _sanitizeSelectionAfterReload() {
    const selected = Array.from(this.selectedNodeIds || [])
      .map(n => Number(n))
      .filter(n => Number.isFinite(n))
      .filter(id => id === 0 || !!this.groupsMap?.[id]);

    const nextSelected = selected.length ? selected : [0];

    let active = Number(this.activeGroupId);
    const activeValid = Number.isFinite(active) && (active === 0 || !!this.groupsMap?.[active]);
    if (!activeValid) active = nextSelected[nextSelected.length - 1] ?? 0;

    this._applySelection(nextSelected, active);
    this._saveSelectionToCache();
  }

  getInvertedGroups(groups) {
    const result = [];

    function traverse(node, ancestors) {
      (node.groups || []).forEach(child => traverse(child, [node, ...ancestors]));

      const fullPath = [node, ...ancestors];

      const branch = fullPath
        .slice(0, -1)
        .map(n => ({ id: n.id, group_name: n.group_name }));

      if (branch.length > 0) {
        result.push(branch);
      }
    }

    groups.forEach(root => traverse(root, []));
    return result;
  }

  async loadGroups() {
    this.groups = [];
    this.groupsMap = {};

    const response = await apiRequest("get", `/groups/sensors`);

    if (response.type === "success") {
      const accounts = Array.isArray(response.data) ? response.data : [];

      // Anything with sub_account_id/account_id === 0 contributes "global" groups
      const globalContainers = accounts.filter(acct => {
        const sid = Number(acct?.sub_account_id ?? acct?.account_id ?? null);
        return Number.isFinite(sid) && sid === 0;
      });

      let globalGroupsRaw = [];
      globalContainers.forEach(acct => {
        if (Array.isArray(acct?.groups)) globalGroupsRaw.push(...acct.groups);
      });

      // Optional alternate payloads (safe no-ops if not present)
      if (!globalGroupsRaw.length && Array.isArray(response?.global_groups)) {
        globalGroupsRaw = response.global_groups;
      }
      if (!globalGroupsRaw.length && Array.isArray(response?.data?.global_groups)) {
        globalGroupsRaw = response.data.global_groups;
      }

      const globalGroups = (globalGroupsRaw || [])
        .map(g => this._normalizeGroupNode(g, 0))
        .filter(Boolean);

      // Real accounts: sub_account_id != 0
      const realAccounts = accounts.filter(acct => {
        const sid = Number(acct?.sub_account_id ?? acct?.account_id ?? null);
        return Number.isFinite(sid) && sid !== 0;
      });

      const accountNodes = realAccounts
        .map(acct => this._makeAccountNode(acct))
        .filter(Boolean);

      // Top-level: All Sensors (no nested groups anymore)
      const allSensorsRoot = {
        id: this.ROOT_SENSORS_ID,
        group_name: "All Sensors",
        node_type: "root",
        visible: 1,
        account_id: 0,
        sub_account_id: 0,
        groups: []
      };

      // Final sidebar order:
      // - All Sensors
      // - Account #1, Account #2, ...
      // - Sensor Group 1, Sensor Group 2, ...
      this.groups = [allSensorsRoot, ...accountNodes, ...globalGroups];
    }

    this._buildGroupsMap();

    // After any reload post-init, keep selection sane (deleted groups, etc)
    if (this._initialized) {
      this._sanitizeSelectionAfterReload();
    }
  }

  _accountNodeId(subAccountId) {
    // account folders get negative IDs to avoid collisions with real group IDs
    return -1 * Number(subAccountId);
  }

  _unmonitoredNodeId(subAccountId) {
    // a different negative range than account folder IDs
    return -1000000000 - Number(subAccountId);
  }

  _resolveSubAccountIdForNodeId(nodeId) {
    const node = this.groupsMap?.[nodeId];
    const v = node?.sub_account_id ?? node?.account_id ?? null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  _getSensorOwnerId(sensor) {
    const v =
      sensor?.owner_id ??
      sensor?.sub_account ??
      sensor?.sub_account_id ??
      sensor?.account_id ??
      sensor?.subAccountId ??
      null;

    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  _resolveGroupIdForSensorClick(sensorId, contextNodeId) {
    const ctx = Number(contextNodeId);

    // If they clicked a sensor that is currently rendered under a real group, use that.
    if (Number.isFinite(ctx) && ctx > 0) return ctx;

    const sensor = this.getSensorById(sensorId);
    if (!sensor) return null;

    const accountId = this._getSensorAccountId(sensor);

    // Prefer groups in the same account; fall back to any groups.
    const gids = Number.isFinite(Number(accountId)) && Number(accountId) > 0
      ? this._sensorGroupIdsInAccount(sensor, accountId)
      : this._sensorGroupIds(sensor);

    if (!gids.length) return 0;

    // Pick the deepest group (most specific) in the tree.
    let best = gids[0];
    let bestDepth = (this._findPath(this.groups, best) || []).length;

    for (const gid of gids) {
      const depth = (this._findPath(this.groups, gid) || []).length;
      if (depth > bestDepth) {
        best = gid;
        bestDepth = depth;
      }
    }

    return best;
  }

  _getSensorAccountId(sensor) {
    // new contract: sensor.sub_account is the sub-account id
    // but keep a couple fallbacks so older payloads don’t break rendering
    const v =
      sensor?.sub_account ??
      sensor?.sub_account_id ??
      sensor?.account_id ??
      sensor?.subAccountId ??
      null;

    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  _sensorGroupIdsInAccount(sensor, accountId) {
    const gids = this._sensorGroupIds(sensor);
    return gids.filter(gid => {
      const node = this.groupsMap?.[gid];
      return node && node.node_type === "group" && Number(node.account_id) === Number(accountId);
    });
  }

  _sensorGroupIds(sensor) {
    // normalize to numbers, ignore junk (supports ids or {id:...} objects)
    const raw = Array.isArray(sensor?.groups) ? sensor.groups : [];
    return raw
      .map(g => this._coerceGroupId(g))
      .filter(n => Number.isFinite(n) && n > 0);
  }

  _sensorHasValidGroupInAccount(sensor, accountId) {
    const gids = this._sensorGroupIds(sensor);
    return gids.some(gid => {
      const node = this.groupsMap[gid];
      return node && node.node_type === "group" && Number(node.account_id) === Number(accountId);
    });
  }

  _getUnmonitoredSensors(accountId) {
    return this.sensors
      .filter(s => Number(this._getSensorAccountId(s)) === Number(accountId))
      .filter(s => !this._sensorHasValidGroupInAccount(s, accountId));
  }

  _normalizeGroupNode(node, accountId) {
    if (!node || typeof node !== "object") return null;

    // normalize name
    if (!node.group_name && node.name) node.group_name = node.name;

    node.node_type = node.node_type || "group";
    node.account_id = accountId;
    node.sub_account_id = accountId;
    node.visible = node.visible == null ? 1 : node.visible;

    if (!Array.isArray(node.groups)) node.groups = [];

    node.groups = node.groups
      .map(child => this._normalizeGroupNode(child, accountId))
      .filter(Boolean);

    return node;
  }


  _makeAccountNode(acct) {
    const accountId = Number(acct?.sub_account_id);

    // Never build an account node for sub_account_id=0 (that belongs to All Sensors tree)
    if (!Number.isFinite(accountId) || accountId <= 0) {
      return null;
    }

    const node = {
      id: this._accountNodeId(accountId),
      group_name: acct?.account_name || `Account ${accountId}`,
      node_type: "account",
      account_id: accountId,
      sub_account_id: accountId,
      visible: 1,
      groups: []
    };

    // Pseudo-node: "All Sensors" (shows every sensor for this account)
    const allSensors = {
      id: this._unmonitoredNodeId(accountId),
      group_name: "Sensors",
      node_type: "all_sensors",
      account_id: accountId,
      sub_account_id: accountId,
      visible: 1,
      groups: []
    };

    const rawGroups = Array.isArray(acct?.groups) ? acct.groups : [];

    // If backend ever leaks sub_account_id=0 groups into accounts, ignore them here
    const normalized = rawGroups
      .filter(g => Number(g?.sub_account_id ?? g?.account_id ?? accountId) !== 0)
      .map(g => this._normalizeGroupNode(g, accountId))
      .filter(Boolean);

    node.groups = [allSensors, ...normalized];
    return node;
  }


  _isRealGroupNode(node) {
    return !!node && node.node_type === "group" && Number.isFinite(Number(node.id)) && Number(node.id) > 0;
  }

  /**
   * Walks the nested `this.groups` tree and populates `this.groupsMap`
   * so that every id => node lookup is O(1).
   */
  _buildGroupsMap() {
    this.groupsMap = {}; // id -> node

    const recurse = (node, parentIds = []) => {
      // 1) register in the map
      this.groupsMap[node.id] = node;

      // 2) record all ancestor IDs
      node.parents = [...parentIds];

      // 3) init children list
      node.children = [];

      // 4) if there are direct sub-groups, walk them
      if (Array.isArray(node.groups)) {
        node.groups.forEach(child => {
          node.children.push(child.id);
          recurse(child, [...parentIds, node.id]);
          node.children.push(...child.children);
        });
      }
    };

    this.groups.forEach(root => recurse(root));
  }

  render() {
    const openIds = Array.from(this.expandedGroupIds);
    const activeIds = Array.from(this.activeGroupIds);
    this.sidebar.innerHTML = this._createList(this.groups);
    this._renderGroupDetails();
    this._attachDroppable();
  }

  /**
   * The sidebar scrolls on its own, so the selected folder can be out of
   * sight: on load (the selection is remembered) or when it is picked from the
   * grid's breadcrumbs. Bring its row into view, clear of the "All Sensors"
   * row pinned to the top; a row already in view is left where it is.
   */
  _revealActiveFolder() {
    const header = this.sidebar.querySelector(
      `li.folder-item[data-for="${this.activeGroupId}"] > .folder-header`
    );
    if (!header) return;

    const pinned = this.sidebar.querySelector(`li.folder-item[data-for="${this.ROOT_SENSORS_ID}"]`);

    revealInScroller(this.sidebar, header, pinned && !pinned.contains(header) ? pinned.offsetHeight : 0);
  }

  /**
   * Make each folder-header accept drops.
   * When something is dropped, it dispatches a `groupDrop` event
   * with { groupId, payload } in detail.
   */
  _attachDroppable() {
    // Remove any prior listeners (if render() is called multiple times)
    const oldHeaders = this.sidebar.querySelectorAll('.folder-header[data-droppable="1"]');
    oldHeaders.forEach(el => {
      el.removeEventListener("dragover", this._onDragOver);
      el.removeEventListener("dragenter", this._onDragEnter);
      el.removeEventListener("dragleave", this._onDragLeave);
      el.removeEventListener("drop", this._onDrop);
    });

    // Attach listeners; bind so we can remove later if needed:
    this._onDragOver = e => {
      e.preventDefault();
      e.currentTarget.classList.add("droppable-over");
    };

    this._onDragEnter = e => {
      e.preventDefault();
      e.currentTarget.classList.add("droppable-over");
    };

    this._onDragLeave = e => {
      e.currentTarget.classList.remove("droppable-over");
    };

    this._onDrop = async e => {
      e.preventDefault();
      e.currentTarget.classList.remove("droppable-over");

      const folderId = Number(e.currentTarget.dataset.id);

      const raw =
        e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain");

      let payload = null;
      try {
        payload = raw ? JSON.parse(raw) : null;
      } catch (err) {
        console.warn("Could not parse dropped data as JSON:", raw);
        return;
      }

      if (!payload || !payload.type || !Array.isArray(payload.ids) || !payload.ids.length) {
        return;
      }

      const bodyKey = payload.type === "accounts" ? "account_ids" : "sensor_ids";
      const sub_account_id_add = this._resolveSubAccountIdForNodeId(folderId);

      // Multi-group behavior for sensors:
// dropping sensors onto a group only adds them to that group (no removals).
      let removeGroupIds = [];

      let idsToAdd = payload.ids;

      if (payload.type === "sensors") {
        const folderNode = this.groupsMap?.[folderId];
        const folderAccountId = Number(folderNode?.account_id ?? folderNode?.sub_account_id);

        idsToAdd = (payload.ids || []).filter(id => {
          const sensor = this.getSensorById(id);

          // If we cannot resolve locally, let API decide
          if (!sensor) return true;

          // Only allow adding sensors from the same account as the target group
          if (Number.isFinite(folderAccountId) && folderAccountId > 0) {
            const sidAccount = Number(this._getSensorAccountId(sensor));
            if (Number.isFinite(sidAccount) && sidAccount !== folderAccountId) {
              return false;
            }
          }

          // No-op per sensor if already in this group
          const gids = this._sensorGroupIds(sensor);
          return !gids.includes(Number(folderId));
        });

        if (!idsToAdd.length) {
          return;
        }
      } else if (payload.sourceGroupId != null) {
        const sg = Number(payload.sourceGroupId);
        if (Number.isFinite(sg) && sg !== folderId) removeGroupIds = [sg];
      }

      document.dispatchEvent(
        new CustomEvent("groupDrop", { detail: { groupId: folderId, payload } })
      );

      const addBody = { [bodyKey]: payload.type === "sensors" ? idsToAdd : payload.ids };
      if (sub_account_id_add != null) addBody.sub_account_id = sub_account_id_add;

      const resAdd = await window.apiRequest(
        "post",
        `/groups/${payload.type}/${folderId}/add`,
        addBody
      );

      // Only remove for non-sensor payloads where we preserved old behavior
      let removeResults = [];
      if (resAdd.type === "success" && removeGroupIds.length) {
        removeResults = await Promise.all(
          removeGroupIds.map(async rid => {
            const sub_account_id_remove = this._resolveSubAccountIdForNodeId(rid);

            const removeBody = { [bodyKey]: payload.ids };
            if (sub_account_id_remove != null) {
              removeBody.sub_account_id = sub_account_id_remove;
            } else if (sub_account_id_add != null) {
              removeBody.sub_account_id = sub_account_id_add;
            }

            const res = await window.apiRequest(
              "post",
              `/groups/${payload.type}/${rid}/remove`,
              removeBody
            );

            return { groupId: rid, res };
          })
        );
      }

      const removeSucceededIds = removeResults
        .filter(x => x?.res?.type === "success")
        .map(x => x.groupId);

      let didSoftUpdate = false;

      if (payload.type === "sensors") {
        let patched = [];

        if (resAdd.type === "success") {
          // local mutation now matches multi-group semantics
          patched = patched.concat(
            this._applySensorGroupChange(idsToAdd, { addGroupId: folderId })
          );
          didSoftUpdate = true;
        }

        if (patched.length) {
          document.dispatchEvent(
            new CustomEvent("sensors:patched", {
              detail: {
                sensor_ids: payload.ids,
                add_group_id: resAdd.type === "success" ? folderId : null,
                remove_group_id: null,
                remove_group_ids: [],
                sensors: patched
              }
            })
          );
        }
      }

      if (didSoftUpdate) {
        this.render();
      }

      if (resAdd.type === "success") {
        this.expandedGroupIds.add(folderId);
        document.dispatchEvent(new CustomEvent("groupUpdated", { detail: { group_id: folderId } }));
      }

      removeSucceededIds.forEach(rid => {
        document.dispatchEvent(new CustomEvent("groupUpdated", { detail: { group_id: rid } }));
      });
    };

    const headers = this.sidebar.querySelectorAll('.folder-header[data-droppable="1"]');
    headers.forEach(headerEl => {
      headerEl.addEventListener("dragover", this._onDragOver);
      headerEl.addEventListener("dragenter", this._onDragEnter);
      headerEl.addEventListener("dragleave", this._onDragLeave);
      headerEl.addEventListener("drop", this._onDrop);
    });

    // 1) Clean up old remove-zone listeners
    const oldZones = this.sidebar.querySelectorAll(".item-remove-zone[data-for]");
    oldZones.forEach(z => {
      z.removeEventListener("dragover", this._onRemoveDragOver);
      z.removeEventListener("dragenter", this._onRemoveDragOver);
      z.removeEventListener("dragleave", this._onRemoveDragLeave);
      z.removeEventListener("drop", this._onRemoveDrop);
    });

    // 2) Define the handlers (store them so you can remove them later)
    this._onRemoveDragOver = e => {
      e.preventDefault();
      e.currentTarget.classList.add("drag-over");
    };
    this._onRemoveDragLeave = e => {
      e.currentTarget.classList.remove("drag-over");
    };
    this._onRemoveDrop = async e => {
      e.preventDefault();
      const zone = e.currentTarget;
      zone.classList.remove("drag-over");

      let payload;
      try {
        payload = JSON.parse(
          e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain")
        );
      } catch (_) {
        /* ignore */
      }

      if (!payload || !payload.type || !Array.isArray(payload.ids) || !payload.ids.length) {
        return;
      }

      let groupId = Number(zone.dataset.for);

      // For multi-select drops, remove from exactly the zone's group.
      // For single-sensor drops, keep the "deepest descendant group" behavior.
      let sensorId = null;

      if (payload.ids.length === 1) {
        sensorId = payload.ids[0];
        const sensor = this.getSensorById(sensorId);

        if (sensor && Array.isArray(sensor.groups)) {
          const descendantGroups = sensor.groups
            .map(g => this._coerceGroupId(g))
            .filter(gid => Number.isFinite(gid) && gid > 0)
            .filter(gid => {
              const path = this._findPath(this.groups, gid) || [];
              return path.includes(groupId);
            });


          if (descendantGroups.length) {
            groupId = descendantGroups.reduce((deepest, gid) => {
              const depthG = (this._findPath(this.groups, gid) || []).length;
              const depthD = (this._findPath(this.groups, deepest) || []).length;
              return depthG > depthD ? gid : deepest;
            }, descendantGroups[0]);
          }
        }
      }

      console.log("REMOVE DROP:", { sensorId, groupId });

      const sub_account_id = this._resolveSubAccountIdForNodeId(groupId);

      const body = { sensor_ids: payload.ids };
      if (sub_account_id != null) body.sub_account_id = sub_account_id;

      const res = await window.apiRequest("post", `/groups/${payload.type}/${groupId}/remove`, body);
      console.log(`/groups/${payload.type}/${groupId}/remove`, res);

      if (res.type === "success") {
        if (payload.type === "sensors") {
          const patched = this._applySensorGroupChange(payload.ids, { removeGroupId: groupId });

          if (patched.length) {
            document.dispatchEvent(
              new CustomEvent("sensors:patched", {
                detail: {
                  sensor_ids: payload.ids,
                  add_group_id: null,
                  remove_group_id: groupId,
                  sensors: patched
                }
              })
            );
          }

          this.render();
        }

        document.dispatchEvent(
          new CustomEvent("groupUpdated", {
            detail: { group_id: groupId }
          })
        );
      }
    };

    // 3) Re-query and reattach
    const zones = this.sidebar.querySelectorAll(".item-remove-zone[data-for]");
    zones.forEach(z => {
      z.addEventListener("dragover", this._onRemoveDragOver);
      z.addEventListener("dragenter", this._onRemoveDragOver);
      z.addEventListener("dragleave", this._onRemoveDragLeave);
      z.addEventListener("drop", this._onRemoveDrop);
    });
  }

  _renderGroupDetails() {
    const container = document.querySelector(".group-details");
    if (!container) return;

    const pathIds = this._findPath(this.groups, this.activeGroupId) || [];
    const nodes = pathIds.map(id => this._findNode(this.groups, id)).filter(Boolean);

    const activeNode = this.groupsMap[this.activeGroupId];
    const canEdit = this._isRealGroupNode(activeNode);

    const crumbsHtml = nodes
      .map((node, i) => {
        const isLast = i === nodes.length - 1;
        const nameEsc = (node.group_name || "").replace(/</g, "&lt;");

        return isLast
          ? `
            <span class="breadcrumb current">
              ${nameEsc}
              ${
            canEdit
              ? `
                <button
                   type="button"
                   class="edit-group-button"
                   title="Group Settings"
                   aria-label="Group Settings"
                   data-id="${this.activeGroupId}">
                     <i class="far fa-cog"></i>
                 </button>
              `
              : ""
          }
            </span>`
          : `<a href="#" class="breadcrumb" data-id="${node.id}">${nameEsc}</a>`;
      })
      .join(" &raquo; ");

    container.innerHTML = crumbsHtml;
  }

  _findPath(groups, targetId, trail = []) {
    for (const g of groups) {
      const here = trail.concat(g.id);
      if (g.id == targetId) return here;
      if (g.groups && g.groups.length) {
        const down = this._findPath(g.groups, targetId, here);
        if (down) return down;
      }
    }
    return null;
  }

  _createList(groupArray, level = 0) {
    let lisHtml = "";

    const sortedGroups = groupArray
      .slice()
      .sort((a, b) => {
        // Top-level order:
        // 1) All Sensors
        // 2) Accounts
        // 3) Global Sensor Groups (account_id/sub_account_id === 0)
        if (level === 0) {
          const rank = g => {
            if (g?.id === this.ROOT_SENSORS_ID) return 0;
            if (g?.node_type === "account") return 1;

            const scopeId = Number(g?.account_id ?? g?.sub_account_id ?? null);
            if (g?.node_type === "group" && Number.isFinite(scopeId) && scopeId === 0) return 2;

            return 3;
          };

          const ra = rank(a);
          const rb = rank(b);
          if (ra !== rb) return ra - rb;
        }

        // keep "Account Sensors" first within each account
        if (a?.node_type === "all_sensors" && b?.node_type !== "all_sensors") return -1;
        if (b?.node_type === "all_sensors" && a?.node_type !== "all_sensors") return 1;

        // legacy: keep Unmonitored last if it ever appears
        if (a?.node_type === "unmonitored" && b?.node_type !== "unmonitored") return 1;
        if (b?.node_type === "unmonitored" && a?.node_type !== "unmonitored") return -1;

        return (a.group_name || "").localeCompare(b.group_name || "");
      });

    sortedGroups.forEach(group => {
      const nodeType = group.node_type || (group.id === 0 ? "root" : "group");

      const isOpen = this.expandedGroupIds.has(group.id);
      // is-active runs up through a selection's ancestors; is-selected is the
      // selection itself
      const isActive = this.activeGroupIds.has(group.id);
      const isSelected = this.selectedNodeIds.has(group.id);

      const isRootSensors = nodeType === "root" && group.id === this.ROOT_SENSORS_ID;
      const isAccount = nodeType === "account";
      const isAccountSensors = nodeType === "all_sensors";

      const scopeId = Number(group.account_id ?? group.sub_account_id ?? null);
      const isTopLevelGlobalGroup =
        level === 0 && nodeType === "group" && Number.isFinite(scopeId) && scopeId === 0;

      // Tooltips carry the name at every width, and are the *only* way to read
      // it once the sidebar is dragged down to the icon rail.
      const groupName = htmlEntities(group.group_name || "");

      let iconHtml = "";

      if (isRootSensors || isAccountSensors) {
        const base = "icon-sidebar-sensors";
        const suffix = isActive ? "-active" : "";
        const src = `${window.site.home}/assets/images/${base}${suffix}.png`;

        iconHtml = `<img
    src="${src}"
    width="18"
    class="folder-caret root-folder-icon"
    data-id="${group.id}"
    title="${groupName}"
    alt=""
  />`;
      } else {
        const caretIcon = isOpen ? "fa-folder-open" : "fa-folder";
        iconHtml = `<i
    class="far ${caretIcon} folder-caret"
    data-id="${group.id}"
    title="${groupName} — ${isOpen ? "Collapse" : "Expand"} folder"
  ></i>`;
      }

      // Sensor sets
      let sensorsForNode = [];
      let totalSensors = 0;

      if (nodeType === "root") {
        totalSensors = this.sensors.length;
      } else if (nodeType === "account") {
        totalSensors = this.sensors.filter(
          s => Number(this._getSensorAccountId(s)) === Number(group.account_id)
        ).length;
      } else if (nodeType === "group") {
        const scopeId = Number(group.account_id ?? group.sub_account_id ?? null);
        const isGlobalScope = Number.isFinite(scopeId) && scopeId === 0;

        const scopeSensors = isGlobalScope
          ? this.sensors
          : this.sensors.filter(
            s => Number(this._getSensorAccountId(s)) === Number(group.account_id)
          );

        // direct sensors (what we show when open)
        sensorsForNode = scopeSensors
          .filter(sensor => {
            const gids = this._sensorGroupIds(sensor);
            return gids.includes(Number(group.id));
          })
          .sort((a, b) => (a.sensor_name || "").localeCompare(b.sensor_name || ""));

        // count includes nested children so Parent count is consistent with Child counts
        const subtree = new Set();
        subtree.add(Number(group.id));
        if (Array.isArray(group.children)) {
          group.children.forEach(id => {
            const n = Number(id);
            if (Number.isFinite(n) && n > 0) subtree.add(n);
          });
        }

        totalSensors = scopeSensors.filter(sensor => {
          const gids = this._sensorGroupIds(sensor);
          return gids.some(gid => subtree.has(Number(gid)));
        }).length;
      } else if (nodeType === "all_sensors" || nodeType === "unmonitored") {
        sensorsForNode = this.sensors
          .filter(s => Number(this._getSensorAccountId(s)) === Number(group.account_id))
          .sort((a, b) => (a.sensor_name || "").localeCompare(b.sensor_name || ""));

        totalSensors = sensorsForNode.length;
      }

      // Children
      let childUL = "";
      if (Array.isArray(group.groups) && group.groups.length) {
        childUL = this._createList(group.groups, level + 1);
      }

      // Parent visibility (guarded)
      let isParentVisible = 1;
      if (group.parent_id && this.groupsMap[group.parent_id]) {
        isParentVisible = this.groupsMap[group.parent_id].visible;
      }

      // Droppable only for real groups (not root/account/unmonitored)
      const droppableAttr = nodeType === "group" ? `data-droppable="1"` : `data-droppable="0"`;

      // Actions: allow add_group on account + real group (so you can create first group under an account)
      const showAdd =
        isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER) &&
        (nodeType === "account" || nodeType === "group" || group.id === this.ROOT_SENSORS_ID);

      const showDelete = isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER) && (nodeType === "group" && group.id !== 0);

      lisHtml += `
      <li class="folder-item ${isOpen ? "open" : "closed"} ${isActive ? "is-active" : ""} ${isSelected ? "is-selected" : ""} level-${level}" data-for="${group.id}">
        <div class="group-line" data-level="${level}"></div>
        <div class="folder-header" data-id="${group.id}" ${droppableAttr}>
          <div class="folder-label" data-id="${group.id}" title="${groupName}">
            ${iconHtml}
            <span class="folder-label-text" data-id="${group.id}" title="${groupName}">${(group.group_name || "").replace(/</g, "&lt;")}</span>
            ${!isAccount ? `<span class="item-count">${totalSensors}</span>` : ''}
          </div>
          <div class="folder-actions">
            ${
        showAdd
          ? `<i class="far fa-folder-plus" data-for="${group.id}" data-action="add_group" title="Add child group"></i>`
          : ``
      }
            ${
        showDelete
          ? `<i class="far fa-folder-times" data-for="${group.id}" data-action="delete_group" title="Remove group"></i>`
          : ``
      }
          </div>
        </div>
        ${childUL}
    `;

      // Only show sensor lists for real groups + unmonitored
      if (isOpen && (nodeType === "group" || nodeType === "all_sensors" || nodeType === "unmonitored")) {
        if (sensorsForNode.length > 0) {
          const sensorsHtml = sensorsForNode
            .map(sensor => {
              const sid = sensor.sensor_id != null ? sensor.sensor_id : sensor.id;
              const name = sensor.sensor_name || "";
              // name first so a truncated label is still readable on hover
              const tooltip = htmlEntities(
                [name, sensor.sensor_mac_addr].filter(Boolean).join(" — ")
              );
              return `
          <div class="folder-item sensor-item" data-for="sensor" data-group="${group.id}" data-sensor="${sid}" 
          ${isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER) ? 'draggable="true"' : ''}
          >
            <div class="item-entry" data-sensor-id="${sid}" title="${tooltip}">
              <img src="${window.site.home}/assets/images/icon-temp-stick-simple.png" />
              <span class="sensor-name" title="${tooltip}">${name}</span>
            </div>
          </div>
        `;
            })
            .join("");

          lisHtml += `
      <div class="folder-item item-entries show">
        ${sensorsHtml}
      </div>
    `;

          // Remove zone only for real groups (Unmonitored is a pseudo view)
          if (nodeType === "group") {
            lisHtml += `
        <div class="item-remove-zone" data-for="${group.id}">
          Remove From <span class="item-remove-zone-name">"${(group.group_name || "").replace(/</g, "&lt;")}"</span>
        </div>
      `;
          }
        } else if (nodeType === "group" && totalSensors === 0) {
          // Only show this if there are NO sensors anywhere in this group’s subtree.
          lisHtml += `
      <div class="folder-item item-entries show">
        <div class="folder-item sensor-item">
          <div class="item-entry no-items">
            <i class="far fa-sensor-alert"></i>
            <span class="sensor-name">No sensors in group</span>
          </div>
        </div>
      </div>
    `;
        }
      }


      lisHtml += `</li>`;
    });

    return lisHtml ? `<ul class="folder-list">${lisHtml}</ul>` : "";
  }

  _closeInlineCreate() {
    const newGroupElements = this.sidebar.querySelectorAll(".folder-item.creating");
    $(".folder-item.spacer").remove();
    for (const el of newGroupElements) {
      $(el).remove();
    }
  }

  _startInlineCreate(parentId, li) {
    const html = `
  <li class="folder-item new-group creating" data-for="${parentId}">
    <div style="display: flex; align-items: center; background-color: #fff;">

      <input
        type="text"
        class="new-group-input"
        placeholder="Name for new group"
        data-for="${parentId}"
      />
      <i class="fas fa-check confirm-icon"
          data-action="create"
          role="button"
          aria-label="Create new group"
          title="Create"></i>
      <i class="fas fa-times cancel-icon"
          data-action="cancel"
          role="button"
          aria-label="Cancel creating group"
          title="Cancel"></i>
    </div>
    <div class="error-message" data-for="new-group-name">Hello I'm an error</div>
  </li>
  `;

    const $li = $(li);
    const $firstUL = $li.children("ul").first();

    if ($firstUL.length) {
      $firstUL.before(html);
    } else {
      $li.append(html);
    }

    const $createLi = $("body").find(".folder-item.new-group.creating");

    // The form spans the sidebar, not the (indented) folder it sits in. It is
    // measured rather than worked out from the depth: the sidebar scrolls, so
    // whatever hangs outside it is cut off (clientWidth leaves out the scrollbar).
    const sidebarStyle = getComputedStyle(this.sidebar);
    const sidebarLeft = this.sidebar.getBoundingClientRect().left + this.sidebar.clientLeft;
    const left = sidebarLeft + (parseFloat(sidebarStyle.paddingLeft) || 0);
    const right = sidebarLeft + this.sidebar.clientWidth - (parseFloat(sidebarStyle.paddingRight) || 0);

    $createLi.css({
      marginLeft: `${Math.round(left - $createLi[0].getBoundingClientRect().left)}px`,
      width: `${Math.round(Math.max(right - left, 150))}px`
    });

    const $name = $createLi.find(".new-group-input");
    const $nameErr = $createLi.find('.error-message[data-for="new-group-name"]');

    $createLi[0]._nameError = errorHelper($name[0], $nameErr[0]);

    $name.trigger("focus");

    $createLi.on("click", '[data-action="create"]', async () => {
      const name = $name.val().trim();

      const parentNodeId = Number($name.data("for"));
      const parent_id = parentNodeId > 0 ? parentNodeId : 0;

      const sub_account_id = this._resolveSubAccountIdForNodeId(parentNodeId);
      if (sub_account_id == null) {
        $createLi[0]._nameError.error("Select an account for this group");
        return;
      }

      if (name.length < 3) {
        $createLi[0]._nameError.error("Select a name of at least three characters");
        return;
      }

      const response = await window.apiRequest("post", `/groups/sensors/create`, {
        group_name: name,
        parent_id,
        sub_account_id
      });

      if (response.type === "error") {
        $createLi[0]._nameError.error(response.message);
      } else {
        await this.loadGroups();
        this.render();
        this._onFolderClick(parentNodeId, "select");
      }
    });

    $createLi.on("click", '[data-action="cancel"]', () => {
      console.log("Cancel create");
      this._closeInlineCreate();
    });

    $createLi.on("keydown", ".new-group-input", e => {
      if (e.key === "Escape") {
        this._closeInlineCreate();
      }
    });
  }

  async _finishInlineCreate() {
    const $createLi = $("body").find(".folder-item.new-group.creating");
    $createLi[0]._nameError.destroy();
    delete $createLi[0]._nameError;

    this.render();
  }

  _findNode(nodes, id) {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.groups.length) {
        const found = this._findNode(n.groups, id);
        if (found) return found;
      }
    }
    return null;
  }

  _onFolderClick(nodeId, mode = "select") {
    console.log({ mode });

    if (!this.selectedNodeIds) this.selectedNodeIds = new Set();

    const targetId = Number(nodeId);

    if (mode === "select") {
      this.selectedNodeIds.clear();
      this.selectedNodeIds.add(targetId);
      this.activeGroupId = targetId;
    } else if (mode === "add") {
      this.selectedNodeIds.add(targetId);
      this.activeGroupId = targetId;
    } else if (mode === "delete") {
      const path = this._findPath(this.groups, targetId) || [];
      const parentId = path.length >= 2 ? path[path.length - 2] : 0;

      const isDescendant = id => {
        const descendantPath = this._findPath(this.groups, id) || [];
        return descendantPath.includes(targetId);
      };

      // collapse subtree
      Array.from(this.expandedGroupIds).forEach(id => {
        if (id === targetId || isDescendant(id)) this.expandedGroupIds.delete(id);
      });

      // remove subtree from explicit selections
      Array.from(this.selectedNodeIds).forEach(id => {
        if (id === targetId || isDescendant(id)) this.selectedNodeIds.delete(id);
      });

      // step selection up to parent
      this.selectedNodeIds.clear();
      this.selectedNodeIds.add(parentId);
      this.activeGroupId = parentId;
    }

    // Highlight: explicit selections + their ancestors
    const newActive = new Set();
    this.selectedNodeIds.forEach(id => {
      const pathIds = this._findPath(this.groups, id) || [];
      pathIds.forEach(pid => newActive.add(pid));
    });
    this.activeGroupIds = newActive;

    // Expand all active nodes so selection is visible
    this.activeGroupIds.forEach(id => this.expandedGroupIds.add(id));

    // Any root select should be exclusive
    const activeNode = this.groupsMap?.[this.activeGroupId];
    if (activeNode && activeNode.node_type === "root" && mode === "select") {
      this.selectedNodeIds.clear();
      this.selectedNodeIds.add(this.activeGroupId);

      this.activeGroupIds.clear();
      this.activeGroupIds.add(this.activeGroupId);
    }

    // Keep top-level roots open
    this.expandedGroupIds.add(this.ROOT_SENSORS_ID);

    const node = this._findNode(this.groups, this.activeGroupId);
    this.currentGroup = node || null;

    const selectedNodeIds = Array.from(this.selectedNodeIds);

    this._saveSelectionToCache();

    this.render();
    this._revealActiveFolder();
    this._emitSelect(selectedNodeIds);
  }

  _emitSelect(selectedNodeIds = []) {
    const nodes = Array.isArray(selectedNodeIds) ? selectedNodeIds : [];

    const groupIds = [];
    const subAccountIds = new Set();
    const unmonitoredAccountIds = new Set();

    nodes.forEach(id => {
      const node = this.groupsMap?.[id];
      if (!node) return;

      if (node.node_type === "group" && Number(id) > 0) {
        groupIds.push(Number(id));
        return;
      }

      if (node.node_type === "account") {
        const aid = Number(node.account_id ?? node.sub_account_id);
        if (Number.isFinite(aid)) subAccountIds.add(aid);
        return;
      }

      if (node.node_type === "all_sensors" || node.node_type === "unmonitored") {
        const aid = Number(node.account_id ?? node.sub_account_id);
        if (Number.isFinite(aid)) {
          subAccountIds.add(aid);
          // NOTE: do not add to unmonitoredAccountIds anymore
        }
      }
    });

    document.dispatchEvent(
      new CustomEvent("groupSelected", {
        detail: {
          groupIds,
          subAccountIds: Array.from(subAccountIds),
          sub_account_ids: Array.from(subAccountIds),
          unmonitoredAccountIds: Array.from(unmonitoredAccountIds),
          unmonitored_account_ids: Array.from(unmonitoredAccountIds)
        }
      })
    );
  }

  _coerceGroupId(v) {
    if (v && typeof v === "object") {
      v = v.id ?? v.group_id ?? v.groupId ?? v.value ?? v;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  _ensureSensorGroups(sensor) {
    if (!sensor || typeof sensor !== "object") return [];

    if (!Array.isArray(sensor.groups)) sensor.groups = [];

    // normalize to numeric, > 0, de-duped
    const out = [];
    const seen = new Set();

    for (const g of sensor.groups) {
      const gid = this._coerceGroupId(g);
      if (!gid || gid <= 0) continue;
      if (seen.has(gid)) continue;
      seen.add(gid);
      out.push(gid);
    }

    sensor.groups = out;
    return out;
  }

  _applySensorGroupChange(sensorIds, { addGroupId = null, removeGroupId = null } = {}) {
    const addId = addGroupId != null ? Number(addGroupId) : null;
    const remId = removeGroupId != null ? Number(removeGroupId) : null;

    const updated = [];

    (sensorIds || []).forEach(id => {
      const sensor = this.getSensorById(id);
      if (!sensor) return;

      const before = this._ensureSensorGroups(sensor).slice();

      let next = before.slice();

      // Additive semantics: when adding a group, keep existing groups.
      if (addId && addId > 0) {
        if (!next.some(gid => Number(gid) === addId)) {
          next.push(addId);
        }
      }

      if (remId && remId > 0) {
        next = next.filter(gid => Number(gid) !== remId);
      }

      sensor.groups = next;
      const after = this._ensureSensorGroups(sensor);

      if (before.join(",") !== after.join(",")) {
        updated.push(sensor);
      }
    });

    return updated;
  }

}
